// SyncStatusChip — cross-device cloud-sync status indicator (B5.4).
//
// Reads the save-slice 5-state machine (saveStatus + pendingCount +
// lastSavedAt + isOnline) and renders a compact indicator so the user can see
// at a glance whether their learning data is synced to the cloud.
//
// Two visual variants:
//   - variant="header"  → pill with icon + label (mirrors HealthBadge). For the
//     global AppHeader (home / history pages).
//   - variant="sidebar" → dot + short label (mirrors the save-status-row mockup
//     in account-system-preview/style-a-ink-seal.html). For the course sidebar
//     footer, which sits on the navy sidebar background.
//
// The save-slice was wired in 19BN Phase 3 but never rendered (the "sidebar
// save-status-row" referenced in save-slice.ts header comment was never built).
// B5.4 completes it: this component is its first and only consumer.
//
// Notification policy: per docs/architecture/notification-strategy.md §S1, sync
// SUCCESS is silent (this chip is the feedback). Sync FAILURE still toasts via
// use-sync-engine.ts (separate path). Hydration completion toasts once via §S2.
//
// Spec: docs/architecture/notification-strategy.md §S1 · docs/architecture/llm-native.md §13.4

import { Cloud, CloudOff, Loader2, Check, AlertCircle } from 'lucide-react';
import { useStore } from '../lib/store/index.js';
import { useT, useLocale } from '../i18n/index.js';
import type { SaveStatus } from '../lib/store/save-slice.js';

export type SyncStatusVariant = 'header' | 'sidebar';

export interface SyncStatusChipProps {
  variant?: SyncStatusVariant;
}

/** Format lastSavedAt (ms epoch) as a short relative string like "2m ago". */
function formatLastSynced(ms: number | null, locale: string): string | null {
  if (ms === null) return null;
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return locale.startsWith('zh') ? '刚刚' : 'just now';
  if (min < 60) return locale.startsWith('zh') ? `${min} 分钟前` : `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return locale.startsWith('zh') ? `${hr} 小时前` : `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return locale.startsWith('zh') ? `${day} 天前` : `${day}d ago`;
}

export function SyncStatusChip({ variant = 'header' }: SyncStatusChipProps) {
  const t = useT();
  const { locale } = useLocale();
  const saveStatus = useStore((s) => s.saveStatus);
  const pendingCount = useStore((s) => s.pendingCount);
  const lastSavedAt = useStore((s) => s.lastSavedAt);
  const isOnline = useStore((s) => s.isOnline);
  const currentUser = useStore((s) => s.currentUser);
  const progressCloudSync = useStore((s) => s.progressCloudSync);

  // Gate: only render for authenticated users who opted into cross-device sync.
  // Anonymous / opted-out users have no cloud sync activity to show; a green
  // "synced" pill would be misleading for them.
  if (!currentUser || currentUser.isAnonymous || !progressCloudSync) return null;

  // Offline takes precedence over save status — if the network is down, the
  // user cares more about that than the last successful sync state.
  if (!isOnline) {
    return renderState('offline', t('sync.statusOffline'), { variant, t, locale, lastSavedAt });
  }

  // Map saveStatus → label. pending shows the count when > 0.
  let label: string;
  if (saveStatus === 'pending') {
    label = t('sync.statusPending', { count: pendingCount });
  } else if (saveStatus === 'idle') {
    label = t('sync.statusIdle');
  } else if (saveStatus === 'saving') {
    label = t('sync.statusSaving');
  } else if (saveStatus === 'saved') {
    label = t('sync.statusSaved');
  } else {
    label = t('sync.statusError');
  }

  return renderState(saveStatus, label, { variant, t, locale, lastSavedAt });
}

interface RenderCtx {
  variant: SyncStatusVariant;
  t: ReturnType<typeof useT>;
  locale: string;
  lastSavedAt: number | null;
}

function renderState(
  state: SaveStatus | 'offline',
  label: string,
  ctx: RenderCtx,
) {
  const { variant, t, locale, lastSavedAt } = ctx;
  const dataState = state; // used for CSS attribute selectors
  const icon = pickIcon(state);

  if (variant === 'sidebar') {
    // Compact: dot + optional short label. Sits on the navy sidebar bg.
    const lastStr = formatLastSynced(lastSavedAt, locale);
    return (
      <span
        className="sync-status-chip sync-status-sidebar"
        data-state={dataState}
        title={lastStr ? t('sync.lastSyncedAt', { time: lastStr }) : label}
      >
        <span className="sync-status-dot" aria-hidden="true" />
      </span>
    );
  }

  // Header variant: pill with icon + label (mirrors HealthBadge).
  return (
    <div
      className={`sync-status-chip sync-status-pill sync-status-${dataState}`}
      title={label}
    >
      {icon}
      <span>{label}</span>
    </div>
  );
}

function pickIcon(state: SaveStatus | 'offline') {
  const size = 14;
  switch (state) {
    case 'saving':
      return <Loader2 size={size} aria-hidden="true" className="sync-status-spin" />;
    case 'saved':
    case 'idle':
      return <Check size={size} aria-hidden="true" />;
    case 'error':
      return <AlertCircle size={size} aria-hidden="true" />;
    case 'offline':
      return <CloudOff size={size} aria-hidden="true" />;
    case 'pending':
    default:
      return <Cloud size={size} aria-hidden="true" />;
  }
}
